import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import type { TExample } from '@atlas/uicorn/preview/modules';
import AtlasDateTime from '@atlas/tool/box/date/AtlasDateTime';
import Tooltip from '@atlas/uicorn/lib/Display/Tooltip';

const RelativeTime = React.memo(function RelativeTime({ date }: TRelativeTimeProps): React.ReactElement {
    const [cachedRelative, setCachedRelative] = useState(() => date.relative);

    useEffect(() => {
        if (cachedRelative.includes('day')) {
            return;
        }

        const timer = setInterval(() => {
            setCachedRelative(date.relative);
        }, RECALCULATE_TIME_INTERVAL);

        return () => clearInterval(timer);
        // This is on purpose, because this effect should only run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const fullDateTime = useMemo(() => date.fullDateTime, [date]);

    // TODO: explore TooltipProps.overlayClassName to avoid injecting a classname into the global style
    return (
        <Tooltip className={RELATIVE_TIME_TOOLTIP_CLASS_NAME} title={fullDateTime}>
            <>{cachedRelative}</>
        </Tooltip>
    );
});

export default RelativeTime;

// #region Helpers

export const RELATIVE_TIME_TOOLTIP_CLASS_NAME = 'relative-time-tooltip';
const RECALCULATE_TIME_INTERVAL = 60 * 1000; // 1 minute

// #endregion

// #region Types

export type TRelativeTimeProps = React.HTMLAttributes<HTMLDivElement> & {
    date: AtlasDateTime;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render() {
        const atlasDateTime = AtlasDateTime.fromUTCString('2023-10-01T12:00:00');
        return <RelativeTime date={atlasDateTime} />;
    },
};

// #endregion

// // #region Tests

if (import.meta.vitest) {
    const mockedToday = new Date('2023-10-05T12:00:00Z');
    const { describe, it, expect, beforeAll, afterAll, vi } = import.meta.vitest;
    describe('RelativeTime', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        beforeAll(() => {
            vi.useFakeTimers();
            vi.setSystemTime(mockedToday);
        });

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });

        afterAll(() => {
            vi.useRealTimers();
        });
    });
}

// // #endregion
